import apiService from 'src/shared/Services/apiService';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  SERVICE SELECTION RECORDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Records what the patient chose BEFORE they reach Stripe, so an abandoned
 *  checkout still leaves a trail and the consent, the selection and the eventual
 *  payment can be tied together server-side.
 *
 *  This is an AUDIT write, not a gate. It must never block, slow or fail the
 *  patient's journey — if the endpoint is down, the patient still proceeds to
 *  payment. Every failure is swallowed and logged, never surfaced.
 */

/** Currency symbol → ISO-4217, for the display strings the catalogue uses. */
const SYMBOL_TO_CURRENCY: Record<string, string> = {
  $: 'USD',
  '₹': 'INR',
  '£': 'GBP',
  '€': 'EUR',
};

/**
 * "$399" → { amountMinor: 39900, currency: 'USD' }
 *
 * The catalogue stores prices as display strings, but the backend expects an
 * integer in the SMALLEST currency unit. Multiplying by 100 in floating point
 * ("19.99" * 100 === 1998.9999…) would truncate to the wrong amount, so the
 * fractional part is parsed as digits and padded instead.
 *
 * Returns null when the string cannot be parsed with confidence — better to
 * omit the amount than to record a wrong one in an audit trail.
 */
export const parseDisplayPrice = (
  display: string,
): { amountMinor: number; currency: string } | null => {
  if (!display) return null;

  const symbol = Object.keys(SYMBOL_TO_CURRENCY).find((sym) => display.includes(sym));
  const currency = symbol ? SYMBOL_TO_CURRENCY[symbol] : null;
  if (!currency) return null;

  // Strip everything but digits, separators and the decimal point.
  const numeric = display.replace(/[^0-9.,]/g, '').replace(/,/g, '');
  if (!numeric) return null;

  const [whole, fraction = ''] = numeric.split('.');
  const wholeUnits = Number(whole);
  if (!Number.isFinite(wholeUnits)) return null;

  // Pad or truncate the fraction to exactly two digits: "5" → 50, "" → 00.
  const minorDigits = `${fraction}00`.slice(0, 2);
  const amountMinor = wholeUnits * 100 + Number(minorDigits);

  return Number.isFinite(amountMinor) ? { amountMinor, currency } : null;
};

export interface RecordSelectionArgs {
  serviceCode: string;
  serviceLabel: string;
  priceId: string;
  /** Display string from the catalogue, e.g. "$399". */
  displayPrice: string;
  /** Links the selection to the consent it was made under, when known. */
  consentArtefactId?: string;
}

/**
 * Fire-and-forget recording of a service selection.
 *
 * Resolves to the selection id on success and null on any failure. Callers are
 * not expected to await it.
 */
export const recordServiceSelectionSafely = async ({
  serviceCode,
  serviceLabel,
  priceId,
  displayPrice,
  consentArtefactId,
}: RecordSelectionArgs): Promise<string | null> => {
  const parsed = parseDisplayPrice(displayPrice);

  try {
    const res = await apiService.recordServiceSelection({
      serviceCode,
      serviceLabel,
      priceId,
      // An unparseable price records as 0 rather than blocking the write: the
      // service the patient chose is the fact worth capturing, and the
      // authoritative amount comes from Stripe at payment time regardless.
      displayPrice: parsed?.amountMinor ?? 0,
      currency: parsed?.currency ?? 'USD',
      ...(consentArtefactId ? { consentArtefactId } : {}),
      clientSelectedAt: new Date().toISOString(),
    });
    return res?.selectionId ?? null;
  } catch {
    console.warn(
      '[service-selection] could not record the selection; the patient journey continues regardless.',
    );
    return null;
  }
};
